import React, { useEffect, useState } from 'react';
import { MachineService } from '../services/MachineService';
import './RawFruit.css';

interface Machine {
  id: string;           // primary key in DB
  machineId: string;    // as per entity
  owner: string;
  possibleFruits: string[];
  createdById: string;  // or 'createdBy' if returning a relation
  createdAt: string;    // or Date
  establishedAt: string; // or Date
}

const MachinePage: React.FC = () => {
  const [machines, setMachines] = useState<Machine[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form fields
  const [machineId, setMachineId] = useState('');
  const [owner, setOwner] = useState('');
  const [possibleFruits, setPossibleFruits] = useState(''); 
  // We'll store as comma-separated string for the form, parse before submit
  const [establishedAt, setEstablishedAt] = useState('');

  // Validation errors
  const [formErrors, setFormErrors] = useState<{
    machineId?: string;
    owner?: string;
    possibleFruits?: string;
    establishedAt?: string;
  }>({});

  // Fetch existing machines
  const fetchMachines = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await MachineService.fetchMachines();
      setMachines(response.data);
    } catch (err: any) {
      console.error('Error fetching machines:', err);
      setError('Failed to load machines');
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormSubmitting(true);

    // Reset errors
    setFormErrors({});

    // Basic validations
    const errors: {
      machineId?: string;
      owner?: string;
      possibleFruits?: string;
      establishedAt?: string;
    } = {};

    if (!machineId.trim()) {
      errors.machineId = 'Machine ID is required';
    }
    if (!owner.trim()) {
      errors.owner = 'Owner is required';
    }
    if (!possibleFruits.trim()) {
      errors.possibleFruits = 'Possible Fruits is required';
    }
    if (!establishedAt) {
      errors.establishedAt = 'Established At date is required';
    }

    setFormErrors(errors);

    // If any errors, stop submission
    if (Object.keys(errors).length > 0) {
      setFormSubmitting(false);
      return;
    }

    // Convert the comma-separated string into an array
    const fruitsArray = possibleFruits
      .split(',')
      .map((fruit) => fruit.trim())
      .filter(Boolean); // remove empty

    const newMachineData = {
      machineId: machineId.trim(),
      owner: owner.trim(),
      possibleFruits: fruitsArray, 
      establishedAt, // might be formatted or a Date object if needed
    };

    try {
      await MachineService.createMachine(newMachineData);
      alert('Machine added successfully!');
      // Optionally reset form
      setMachineId('');
      setOwner('');
      setPossibleFruits('');
      setEstablishedAt('');
    } catch (err) {
      console.error('Error creating machine:', err);
      setError('Failed to create machine');
    } finally {
        // Hide form
      setIsFormVisible(false);
      setFormSubmitting(false);
      // Fetch updated list
      fetchMachines();
    }
  };

  // Load machines on mount
  useEffect(() => {
    fetchMachines();
  }, []);

  return (
    <div className="page">
        <div className="container">
      <div className="header">
        <button
          className="button"
          style={{ position: 'absolute', top: '10px', right: '10px' }}
          onClick={() => setIsFormVisible(true)}
        >
          Add Machine
        </button>
      </div>

      {loading ? (
        <p>Loading machines...</p>
      ) : error ? (
        <p style={{ color: 'red' }}>{error}</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Machine ID</th>
              <th>Owner</th>
              <th>Possible Fruits</th>
              <th>Created By</th>
              <th>Created At</th>
              <th>Established At</th>
            </tr>
          </thead>
          <tbody>
            {machines.map((m) => (
              <tr key={m.id}>
                <td>{m.machineId}</td>
                <td>{m.owner}</td>
                <td>
                  {m.possibleFruits && m.possibleFruits.length
                    ? m.possibleFruits.join(', ')
                    : 'N/A'}
                </td>
                <td>{m.createdById || 'N/A'}</td>
                <td>{m.createdAt}</td>
                <td>{m.establishedAt}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {isFormVisible && (
        <div className="form-popup">
          <form onSubmit={handleSubmit} className="form">
            <h2>Add a New Machine</h2>

            <label className="label">Machine ID:</label>
            <input
              type="text"
              className="input"
              value={machineId}
              onChange={(e) => setMachineId(e.target.value)}
            />
            {formErrors.machineId && (
              <p className="error">{formErrors.machineId}</p>
            )}

            <label className="label">Owner:</label>
            <input
              type="text"
              className="input"
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
            />
            {formErrors.owner && <p className="error">{formErrors.owner}</p>}

            <label className="label">Possible Fruits (comma-separated):</label>
            <input
              type="text"
              className="input"
              placeholder="e.g. Apple, Orange, Banana"
              value={possibleFruits}
              onChange={(e) => setPossibleFruits(e.target.value)}
            />
            {formErrors.possibleFruits && (
              <p className="error">{formErrors.possibleFruits}</p>
            )}

            <label className="label">Established At:</label>
            <input
              type="date"
              className="input"
              value={establishedAt}
              onChange={(e) => setEstablishedAt(e.target.value)}
            />
            {formErrors.establishedAt && (
              <p className="error">{formErrors.establishedAt}</p>
            )}

            <button type="submit" className="button" disabled={formSubmitting}>
              {formSubmitting ? 'Submitting...' : 'Submit'}
            </button>
            <button
              type="button"
              className="button cancel"
              onClick={() => setIsFormVisible(false)}
            >
              Cancel
            </button>
          </form>
        </div>
      )}
      </div>
    </div>
  );
};

export default MachinePage;
